import moment from "moment-timezone";

export interface NormalizeDateRangeForTimezoneParams {
  from: string | Date;
  to?: string | Date;
  snapToRangeBoundaries?: boolean;
}
/**
 * Normalizes a date range using the browser's current IANA timezone.
 *
 * When `snapToRangeBoundaries` is enabled (default), the returned range is expanded
 * to full-day boundaries in local time:
 * - `from` -> start of day
 * - `to` -> end of day
 *
 * If `to` is omitted, it defaults to `from`, allowing single-date filters to become
 * a full-day range.
 *
 * @param params Input range values and behavior flags.
 * @param params.from Start date value as a `Date` or parseable date string.
 * @param params.to Optional end date value as a `Date` or parseable date string.
 * @param params.snapToRangeBoundaries Whether to snap to local day boundaries.
 * Defaults to `true`.
 * @returns A normalized range object with `from` and `to` values.
 */
export const normalizeDateRangeForTimezone = ({
  from, to, snapToRangeBoundaries = true
}: NormalizeDateRangeForTimezoneParams): { from: Date; to: Date } => {
  let result: {
    from: Date;
    to: Date;
  };

  if (!to) {
    to = from;
  }

  const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  if (snapToRangeBoundaries) {
    result = {
      from: moment.tz(from, timeZone).startOf("day").toDate(),
      to: moment.tz(to, timeZone).endOf("day").toDate()
    };
  } else {
    result = {
      from: moment.tz(from, timeZone).toDate(),
      to: moment.tz(to, timeZone).toDate()
    };
  }

  return result;
};